import React from 'react';
import styled from 'styled-components';

const Section = styled.section`
  background: white;
  border-radius: 20px;
  padding: 1.75rem;
  box-shadow: 0 18px 35px rgba(15, 23, 42, 0.12);
  border: 1px solid #e5e7eb;
  margin-bottom: 2rem;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: #6b7280;
  max-width: 520px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.85rem;
  border-radius: 9999px;
  background: rgba(99, 102, 241, 0.12);
  color: #4338ca;
  font-size: 0.85rem;
  font-weight: 600;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.25rem;
`;

const Card = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 1.25rem;
  border: 1px solid #e5e7eb;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
`;

const CardText = styled.p`
  margin: 0;
  color: #4b5563;
  line-height: 1.55;
  font-size: 0.95rem;
`;

const MetaBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Label = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
`;

const Value = styled.span`
  font-size: 0.95rem;
  color: #1f2937;
  font-weight: 600;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const ListItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  color: #374151;
  font-size: 0.95rem;
  line-height: 1.4;

  &:before {
    content: '•';
    color: #6366f1;
    font-weight: 700;
    line-height: 1;
    margin-right: 0.2rem;
  }
`;

const InlineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  color: #1f2937;
  font-size: 0.95rem;
`;

const LeadershipItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem;
  border-radius: 12px;
  background: white;
  border: 1px solid #e5e7eb;
`;

const LeadershipName = styled.span`
  font-weight: 700;
  color: #1f2937;
`;

const LeadershipMeta = styled.span`
  font-size: 0.85rem;
  color: #4b5563;
`;

const AboutHospitalCard = ({ profile, variant = 'patient', className }) => {
  if (!profile) return null;

  const {
    name,
    tagline,
    mission,
    address = {},
    contact = {},
    emergency = {},
    hours = {},
    specialties = [],
    services = [],
    leadership = [],
    accreditation = []
  } = profile;

  const specialtiesToShow =
    variant === 'doctor' ? specialties : specialties.slice(0, 3);
  const servicesToShow =
    variant === 'doctor' ? services : services.slice(0, 3);
  const leadershipToShow =
    variant === 'doctor' ? leadership.slice(0, 4) : leadership.slice(0, 2);
  const accreditationToShow =
    variant === 'doctor' ? accreditation : accreditation.slice(0, 2);

  const getAddressString = () => {
    const lines = [
      [address.line1, address.line2].filter(Boolean).join(', '),
      [address.city, address.state, address.postalCode].filter(Boolean).join(', '),
      address.country
    ].filter(Boolean);

    return lines.join('\n');
  };

  return (
    <Section className={className}>
      <Header>
        <TitleGroup>
          <Title>About Us</Title>
          {(name || mission) && (
            <Subtitle>
              {mission ||
                'We deliver patient-centred care with comprehensive clinical programs and an integrated support experience.'}
            </Subtitle>
          )}
        </TitleGroup>
        {tagline && <Badge>{tagline}</Badge>}
      </Header>

      <Grid>
        <Card>
          <CardTitle>Mission & Vision</CardTitle>
          <CardText>
            {mission ||
              'Our mission statement will appear here when configured by the administrative team.'}
          </CardText>
          {accreditationToShow.length > 0 && (
            <MetaBlock>
              <Label>Accreditations</Label>
              <InlineList>
                {accreditationToShow.map((item, index) => (
                  <span key={`accreditation-${index}`}>{item}</span>
                ))}
              </InlineList>
            </MetaBlock>
          )}
        </Card>

        <Card>
          <CardTitle>Contact</CardTitle>
          <MetaBlock>
            <Label>General Line</Label>
            <Value>{contact.mainLine || 'Not available'}</Value>
          </MetaBlock>
          <MetaBlock>
            <Label>Appointments</Label>
            <Value>{contact.appointmentCenter || 'Not available'}</Value>
          </MetaBlock>
          <MetaBlock>
            <Label>Email</Label>
            <Value>{contact.email || 'Not available'}</Value>
          </MetaBlock>
        </Card>

        <Card>
          <CardTitle>Visit Us</CardTitle>
          <CardText style={{ whiteSpace: 'pre-line' }}>
            {getAddressString() || 'Hospital address will be displayed here once configured.'}
          </CardText>
          <MetaBlock>
            <Label>Emergency Hotline</Label>
            <Value>{emergency.hotline || 'Not available'}</Value>
          </MetaBlock>
        </Card>

        <Card>
          <CardTitle>Operating Hours</CardTitle>
          <MetaBlock>
            <Label>Outpatient Clinics</Label>
            <Value>{hours.outpatient || '—'}</Value>
          </MetaBlock>
          <MetaBlock>
            <Label>Emergency</Label>
            <Value>{hours.emergency || '—'}</Value>
          </MetaBlock>
          <MetaBlock>
            <Label>Pharmacy</Label>
            <Value>{hours.pharmacy || '—'}</Value>
          </MetaBlock>
          <MetaBlock>
            <Label>Diagnostics</Label>
            <Value>{hours.diagnostics || '—'}</Value>
          </MetaBlock>
        </Card>

        {specialtiesToShow.length > 0 && (
          <Card>
            <CardTitle>Key Specialties</CardTitle>
            <List>
              {specialtiesToShow.map((item, index) => (
                <ListItem key={`specialty-${index}`}>{item}</ListItem>
              ))}
            </List>
          </Card>
        )}

        {servicesToShow.length > 0 && (
          <Card>
            <CardTitle>Signature Services</CardTitle>
            <List>
              {servicesToShow.map((item, index) => (
                <ListItem key={`service-${index}`}>{item}</ListItem>
              ))}
            </List>
          </Card>
        )}

        {variant === 'doctor' && leadershipToShow.length > 0 && (
          <Card>
            <CardTitle>Leadership Team</CardTitle>
            <InlineList>
              {leadershipToShow.map((leader, index) => (
                <LeadershipItem key={`leader-${index}`}>
                  <LeadershipName>{leader.name}</LeadershipName>
                  {leader.title && <LeadershipMeta>{leader.title}</LeadershipMeta>}
                  {(leader.phone || leader.email) && (
                    <LeadershipMeta>
                      {[leader.phone, leader.email].filter(Boolean).join(' • ')}
                    </LeadershipMeta>
                  )}
                </LeadershipItem>
              ))}
            </InlineList>
          </Card>
        )}
      </Grid>
    </Section>
  );
};

export default AboutHospitalCard;

